import { z } from 'zod';
import { db } from '@/shared/db/database';

export const QUALITY_SETTINGS_KEY = 'mi_quality_settings';

// ── Zod Schemas ───────────────────────────────────────────────────────────────

const QualityThresholdsSchema = z.object({
  blurThreshold: z.number().min(0).max(800),
  brightnessMin: z.number().min(20).max(180),
  brightnessMax: z.number().min(100).max(254),
  faceMinPx: z.number().min(40).max(300),
  yawThresholdDeg: z.number().min(10).max(45),
  pitchThresholdDeg: z.number().min(10).max(45),
  livenessDirectionTimeoutMs: z.number().min(1000).max(10_000),
});

const PersistedQualitySettingsSchema = z.object({
  thresholds: QualityThresholdsSchema,
  livenessMode: z.enum(['disabled', 'minimum', 'strong']),
  qualityGateTimeoutMs: z.number().positive(),
  matchThreshold: z.number().min(0.20).max(0.90),
  // NEW Slice 3: Liveness Aggregator fields (default per design D3)
  passiveHigh: z.number().min(0.50).max(1).default(0.85),
  passiveLow: z.number().min(0.01).max(1).default(0.40),
  livenessWindowSize: z.number().int().min(2).max(15).default(5),
  livenessLiveCount: z.number().int().min(2).max(10).default(3),
  livenessSpoofConsecutive: z.number().int().min(1).max(10).default(3),
  livenessMaxTotalFrames: z.number().int().min(10).max(100).default(30),
  // NEW Slice 3: Gate B overrides (defaults per spec)
  blurMinLaplacianVar: z.number().min(0).max(800).default(100),
  brightnessMinGateB: z.number().min(20).max(180).default(40),
  brightnessMaxGateB: z.number().min(100).max(254).default(220),
  faceMinShortSidePx: z.number().min(40).max(300).default(112),
  // Modo home — default true: HomePage shows the single blue Automático
  // button and the system decides entrada/salida from the person's last
  // timelog. Set to false to fall back to the classic manual pair
  // (Marcar Entrada + Marcar Salida).
  homeButtonAuto: z.boolean().default(true),
  // Consecutive timelog validation — minimum seconds between same accessType (offline only)
  consecutiveTimelogMinSeconds: z.number().int().min(90).max(300).default(90),
  // Quality preset — 'default' for normal conditions, 'high-contrast' for bright/overexposed environments
  qualityPreset: z.enum(['default', 'high-contrast']).default('default'),
  // Frame throttle FPS — frames per second for inference (default 5, lower for low-end devices)
  frameThrottleFps: z.number().int().min(1).max(10).default(5),
}).refine(
  (data) => data.passiveLow < data.passiveHigh,
  { message: 'passiveLow must be less than passiveHigh', path: ['passiveHigh'] },
).refine(
  (data) => data.livenessLiveCount <= data.livenessWindowSize,
  { message: 'livenessLiveCount cannot exceed livenessWindowSize', path: ['livenessLiveCount'] },
).refine(
  (data) => data.livenessSpoofConsecutive <= data.livenessWindowSize,
  { message: 'livenessSpoofConsecutive cannot exceed livenessWindowSize', path: ['livenessSpoofConsecutive'] },
);

// ── TypeScript Interface (from Zod) ────────────────────────────────────────────

export type PersistedQualitySettings = z.infer<typeof PersistedQualitySettingsSchema>;

// ── Service ───────────────────────────────────────────────────────────────────

export const qualitySettingsService = {
  async load(): Promise<PersistedQualitySettings | null> {
    const record = await db.settings.get(QUALITY_SETTINGS_KEY);
    if (!record) return null;
    try {
      const raw = JSON.parse(record.value);
      // Zod parse with defaults hydrates missing fields
      return PersistedQualitySettingsSchema.parse(raw);
    } catch {
      return null; // Malformed or invalid → return null (triggers fallback to defaults)
    }
  },

  async save(settings: PersistedQualitySettings): Promise<void> {
    // Validate before saving (throws on cross-field validation errors)
    const validated = PersistedQualitySettingsSchema.parse(settings);
    await db.settings.put({
      key: QUALITY_SETTINGS_KEY,
      value: JSON.stringify(validated),
      updated_at: Date.now(),
    });
  },

  async reset(): Promise<void> {
    await db.settings.delete(QUALITY_SETTINGS_KEY);
  },
};
